import { NonNegIntSchema, OptionalHexColorSchema, PositiveIntSchema } from "#schemas/primitives.ts";
import { domainRegistry } from "#schemas/registry.ts";
import { z } from "zod";

/** Runtime layer used inside the editor (not the file-format metadata). */
export const LayerSchema = z.object({
  id: z.string().min(1),
  name: z.string().optional(),
  visible: z.boolean(),
  opacity: z.number().min(0).max(1).optional(),
  data: z.array(z.array(OptionalHexColorSchema)),
}).register(domainRegistry, {
  id: "layer",
  title: "Layer",
  description: "Runtime pixel layer (2D color grid).",
  surface: "store",
  llmExposed: false,
  group: "canvas",
});
export type LayerShape = z.infer<typeof LayerSchema>;

export const PxpfAnimationSchema = z.object({
  name: z.string(),
  from: NonNegIntSchema,
  to: NonNegIntSchema,
  fps: z.number(),
  loop: z.enum(["forward", "reverse", "pingpong"]),
}).register(domainRegistry, {
  id: "pxpf_animation",
  title: "PXPF animation",
  description: "Named frame range with fps and loop direction.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

/** Reference to a catalog-registered guard or action by id, with optional JSON params. */
export const CatalogRefSchema = z.object({
  id: z.string().min(1),
  params: z.unknown().optional(),
}).register(domainRegistry, {
  id: "catalog_ref",
  title: "Catalog reference",
  description: "Reference to an app-registered guard or action by id, with optional JSON params.",
  surface: "pxpf",
  llmExposed: false,
  group: "fsm",
});
export type CatalogRef = z.infer<typeof CatalogRefSchema>;

export const PxpfTransitionSchema = z.object({
  from: z.string(),
  to: z.string(),
  event: z.string(),
  trigger: z.enum(["onLoopEnd", "manual"]),
  guard: CatalogRefSchema.optional(),
  actions: z.array(CatalogRefSchema).optional(),
}).register(domainRegistry, {
  id: "pxpf_transition",
  title: "PXPF transition",
  description: "State-machine transition declared in the file format. Guards/actions reference catalog ids.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

export const PxpfLayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  visible: z.boolean(),
  opacity: z.number(),
}).register(domainRegistry, {
  id: "pxpf_layer",
  title: "PXPF layer",
  description: "File-format layer metadata (no pixel data).",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

export const PxpfFrameSchema = z.object({
  duration: z.number(),
  layers: z.record(z.string(), NonNegIntSchema),
}).register(domainRegistry, {
  id: "pxpf_frame",
  title: "PXPF frame",
  description: "File-format frame: duration + per-layer-id index into layerFrames[].",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

export const PxpfVariantSchema = z.object({
  id: z.string(),
  remap: z.record(z.string(), z.string()).optional(),
}).register(domainRegistry, {
  id: "pxpf_variant",
  title: "PXPF variant",
  description: "Palette-remapped variant of the base frames.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

export const CanvasSchema = z.object({
  rows: PositiveIntSchema,
  cols: PositiveIntSchema,
}).register(domainRegistry, {
  id: "pxpf_canvas",
  title: "PXPF canvas",
  description: "Canvas dimensions.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});

/** Per-state metadata: entry/exit action lists. State id is implicit from the parent record key in usage,
 * but we carry it on the node for serialization consistency with `transitions[]`. */
export const StateNodeSchema = z.object({
  id: z.string().min(1),
  entry: z.array(CatalogRefSchema).optional(),
  exit: z.array(CatalogRefSchema).optional(),
}).register(domainRegistry, {
  id: "pxpf_state_node",
  title: "PXPF state node",
  description: "Per-state metadata (entry/exit actions). Hierarchical/nested support reserved for later.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});
export type StateNode = z.infer<typeof StateNodeSchema>;

export const StateMachineSchema = z.object({
  initial: z.string(),
  states: z.array(StateNodeSchema).optional(),
  transitions: z.array(PxpfTransitionSchema),
}).register(domainRegistry, {
  id: "pxpf_state_machine",
  title: "PXPF state machine",
  description: "Initial state, optional per-state node metadata, and transition table. Flat (non-hierarchical) for v1.",
  surface: "pxpf",
  llmExposed: false,
  group: "pxpf",
});
export type StateMachine = z.infer<typeof StateMachineSchema>;

/** xyflow node positions per state id. Decoupled from machine semantics so undo can skip layout-only edits. */
export const MachineLayoutSchema = z
  .record(z.string(), z.object({ x: z.number(), y: z.number() }))
  .register(domainRegistry, {
    id: "machine_layout",
    title: "Machine layout",
    description: "xyflow node positions keyed by state id.",
    surface: "store",
    llmExposed: false,
    persistKey: "machineLayout",
    group: "fsm",
  });
export type MachineLayout = z.infer<typeof MachineLayoutSchema>;

/**
 * Collect every state id mentioned by a StateMachine: initial, transition endpoints, and explicit state nodes.
 * Used by validators (every stateFrames key must be a state) and by the build factory (xstate config requires
 * every referenced state to be declared).
 */
export function collectStateIds(machine: StateMachine): Set<string> {
  const ids = new Set<string>();
  ids.add(machine.initial);
  for (const t of machine.transitions) {
    ids.add(t.from);
    ids.add(t.to);
  }
  if (machine.states) {
    for (const s of machine.states) ids.add(s.id);
  }
  return ids;
}

const PxpfBaseSchema = z.object({
  v: z.number(),
  name: z.string(),
  canvas: CanvasSchema,
  tileSize: PositiveIntSchema,
  palette: z.array(z.string()),
  tiles: z.array(z.string()),
  layerFrames: z.array(z.object({ tilemap: z.array(NonNegIntSchema) })),
  layers: z.array(PxpfLayerSchema),
  frames: z.array(PxpfFrameSchema),
  animations: z.array(PxpfAnimationSchema),
  stateMachine: StateMachineSchema,
  machineLayout: MachineLayoutSchema,
  variants: z.array(PxpfVariantSchema),
});

/**
 * Expand a palette-indexed RLE string ("idx:count idx:count ...") into a flat
 * array of palette indices. Throws when the encoded length differs from
 * `expectedLength` or any token is malformed. Empty `rle` is valid only when
 * `expectedLength === 0`.
 *
 * Index 0 is reserved for "transparent"; indices >= 1 are palette offsets
 * (caller-defined). This helper intentionally does no palette-bounds check —
 * callers that need it (e.g. the PXPF schema validator) should track maxIndex
 * themselves via `rleExpandedLength` or compute it on the returned array.
 */
export function expandRle(rle: string, expectedLength: number): number[] {
  const out: number[] = [];
  if (rle.length === 0) {
    if (expectedLength === 0) return out;
    throw new Error(`RLE empty but expected ${String(expectedLength)} cells`);
  }
  for (const token of rle.split(" ")) {
    if (token === "") continue;
    const sep = token.indexOf(":");
    if (sep <= 0 || sep === token.length - 1) {
      throw new Error(`malformed RLE token "${token}"`);
    }
    const idx = Number(token.slice(0, sep));
    const count = Number(token.slice(sep + 1));
    if (!Number.isInteger(idx) || idx < 0) throw new Error(`invalid RLE index in "${token}"`);
    if (!Number.isInteger(count) || count < 0) throw new Error(`invalid RLE count in "${token}"`);
    for (let n = 0; n < count; n += 1) out.push(idx);
  }
  if (out.length !== expectedLength) {
    throw new Error(`RLE expanded to ${String(out.length)} cells, expected ${String(expectedLength)}`);
  }
  return out;
}

function rleExpandedLength(rle: string): { length: number; maxIndex: number } | null {
  if (rle === "") return { length: 0, maxIndex: -1 };
  let length = 0;
  let maxIndex = -1;
  for (const tok of rle.split(" ")) {
    const parts = tok.split(":");
    const iStr = parts[0];
    const cStr = parts[1];
    if (iStr === undefined || cStr === undefined) return null;
    const idx = Number(iStr);
    const count = Number(cStr);
    if (!Number.isFinite(idx) || !Number.isFinite(count)) return null;
    if (!Number.isInteger(idx) || !Number.isInteger(count)) return null;
    if (idx < 0 || count < 0) return null;
    length += count;
    if (idx > maxIndex) maxIndex = idx;
  }
  return { length, maxIndex };
}

export const PxpfSchema = PxpfBaseSchema.check((ctx) => {
  const data = ctx.value;
  const { canvas, tileSize, palette, tiles, layerFrames, frames, layers, animations } = data;

  if (tileSize !== 1) {
    if (canvas.rows % tileSize !== 0 || canvas.cols % tileSize !== 0) {
      ctx.issues.push({
        code: "custom",
        path: ["tileSize"],
        message: `tileSize ${tileSize} must divide rows (${canvas.rows}) and cols (${canvas.cols}) evenly`,
        input: tileSize,
      });
    }
  }

  if (palette.length === 0) {
    ctx.issues.push({
      code: "custom",
      path: ["palette"],
      message: "palette must not be empty",
      input: palette,
    });
  } else if (palette[0] !== "") {
    ctx.issues.push({
      code: "custom",
      path: ["palette", 0],
      message: "palette[0] must be \"\" (transparent sentinel)",
      input: palette[0],
    });
  }

  const expectedCells = tileSize * tileSize;
  tiles.forEach((rle, i) => {
    const info = rleExpandedLength(rle);
    if (info === null) {
      ctx.issues.push({
        code: "custom",
        path: ["tiles", i],
        message: "malformed RLE token",
        input: rle,
      });
      return;
    }
    if (info.length !== expectedCells) {
      ctx.issues.push({
        code: "custom",
        path: ["tiles", i],
        message: `tile RLE expands to ${info.length} cells, expected ${expectedCells}`,
        input: rle,
      });
    }
    if (info.maxIndex >= palette.length) {
      ctx.issues.push({
        code: "custom",
        path: ["tiles", i],
        message: `tile RLE references palette index ${info.maxIndex} >= palette.length ${palette.length}`,
        input: rle,
      });
    }
  });

  layerFrames.forEach((lf, lfi) => {
    lf.tilemap.forEach((t, ti) => {
      if (t >= tiles.length) {
        ctx.issues.push({
          code: "custom",
          path: ["layerFrames", lfi, "tilemap", ti],
          message: `tilemap index ${t} >= tiles.length ${tiles.length}`,
          input: t,
        });
      }
    });
  });

  const layerIdSet = new Set(layers.map((l) => l.id));
  frames.forEach((f, fi) => {
    for (const [id, idx] of Object.entries(f.layers)) {
      if (idx >= layerFrames.length) {
        ctx.issues.push({
          code: "custom",
          path: ["frames", fi, "layers", id],
          message: `frame layer index ${idx} >= layerFrames.length ${layerFrames.length}`,
          input: idx,
        });
      }
      if (!layerIdSet.has(id)) {
        ctx.issues.push({
          code: "custom",
          path: ["frames", fi, "layers", id],
          message: `frame references unknown layer id "${id}" (not present in layers[])`,
          input: id,
        });
      }
    }
  });

  animations.forEach((a, ai) => {
    if (a.from > a.to) {
      ctx.issues.push({
        code: "custom",
        path: ["animations", ai],
        message: `animation from (${a.from}) > to (${a.to})`,
        input: a,
      });
    }
    if (a.from >= frames.length) {
      ctx.issues.push({
        code: "custom",
        path: ["animations", ai, "from"],
        message: `from ${a.from} out of range (frames.length=${frames.length})`,
        input: a.from,
      });
    }
    if (a.to >= frames.length) {
      ctx.issues.push({
        code: "custom",
        path: ["animations", ai, "to"],
        message: `to ${a.to} out of range (frames.length=${frames.length})`,
        input: a.to,
      });
    }
  });
}).register(domainRegistry, {
  id: "pxpf",
  title: "PXPF document",
  description: "Pixelpuf project file format. Validates structural and cross-field invariants.",
  surface: "pxpf",
  llmExposed: true,
  group: "pxpf",
});

export type PxpfAnimation = z.infer<typeof PxpfAnimationSchema>;
export type PxpfTransition = z.infer<typeof PxpfTransitionSchema>;
export type PxpfLayer = z.infer<typeof PxpfLayerSchema>;
export type PxpfFrame = z.infer<typeof PxpfFrameSchema>;
export type PxpfVariant = z.infer<typeof PxpfVariantSchema>;
export type Pxpf = z.infer<typeof PxpfSchema>;
